import { useEffect, useState } from "react";
import Modal, { FormModalControlled } from "../../components/Modal";
import Input from "../../components/Input";
import Button from "../../components/Button";
import Label from "../../components/Label";

const makeId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const WizardEditorModal = ({ isOpen, onClose, wizardParam, onSave }) => {
  const [steps, setSteps] = useState([]);
  const [showFormatChoice, setShowFormatChoice] = useState(false);

  useEffect(() => {
    if (isOpen && wizardParam) {
      const existingSteps = wizardParam.steps.map((s) => ({
        ...s,
        options: [...s.options],
      }));
      setSteps(existingSteps);
      // Show format choice only for new wizards (no steps yet)
      setShowFormatChoice(existingSteps.length === 0);
    } else if (!isOpen) {
      setSteps([]);
      setShowFormatChoice(false);
    }
    // eslint-disable-next-line
  }, [isOpen]);

  if (!isOpen) return null;

  if (showFormatChoice) {
    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <div style={{ padding: "var(--gap)" }}>
          <h2 style={{ margin: 0 }}>{wizardParam?.label || "Vedlys"}</h2>
          <p style={{ margin: "var(--gap) 0 0", opacity: 0.8 }}>
            Pasirinkite vedlio formatą
          </p>
          <div
            style={{
              display: "flex",
              gap: "var(--gap)",
              margin: "var(--gap) 0",
            }}
          >
            <Button
              important
              onClick={() => setShowFormatChoice(false)}
              style={{ flexGrow: 1 }}
            >
              Paprastas formatas
            </Button>
            <Button
              onClick={() => setShowFormatChoice(false)}
              style={{ flexGrow: 1 }}
            >
              HTML šablonas
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  const handleStepLabelChange = (stepId, label) => {
    setSteps((prev) =>
      prev.map((s) => (s.id === stepId ? { ...s, label } : s)),
    );
  };

  const handleDeleteStep = (stepId) => {
    setSteps((prev) => prev.filter((s) => s.id !== stepId));
  };

  const handleAddOption = (stepId, value) => {
    if (!value.trim()) return;
    setSteps((prev) =>
      prev.map((s) =>
        s.id === stepId
          ? { ...s, options: [...s.options, value.trim()] }
          : s,
      ),
    );
  };

  const handleDeleteOption = (stepId, optionIdx) => {
    setSteps((prev) =>
      prev.map((s) =>
        s.id === stepId
          ? { ...s, options: s.options.filter((_, i) => i !== optionIdx) }
          : s,
      ),
    );
  };

  const handleAddStep = () => {
    setSteps((prev) => [
      ...prev,
      { id: `step_${makeId()}`, label: "", options: [] },
    ]);
  };

  const handleSubmit = () => {
    onSave?.(steps);
    onClose?.();
  };

  return (
    <FormModalControlled
      isOpen={isOpen}
      onClose={onClose}
      onCancel={onClose}
      onSubmit={handleSubmit}
      submitLabel="Gerai"
      cancelLabel="Atšaukti"
    >
      <h2>{wizardParam?.label || "Vedlys"}</h2>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--gap)",
          margin: "var(--gap) 0",
          minWidth: "min(520px, 90vw)",
        }}
      >
        {steps.map((step, stepIdx) => (
          <StepEditor
            key={step.id}
            step={step}
            stepNumber={stepIdx + 1}
            onLabelChange={(label) => handleStepLabelChange(step.id, label)}
            onDelete={() => handleDeleteStep(step.id)}
            onAddOption={(value) => handleAddOption(step.id, value)}
            onDeleteOption={(optIdx) => handleDeleteOption(step.id, optIdx)}
          />
        ))}

        <Button
          inline
          emphasized
          icon="plus"
          onClick={handleAddStep}
        >
          Pridėti žingsnį
        </Button>
      </div>
    </FormModalControlled>
  );
};

const StepEditor = ({
  step,
  stepNumber,
  onLabelChange,
  onDelete,
  onAddOption,
  onDeleteOption,
}) => {
  const [newOption, setNewOption] = useState("");

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--gap-sm)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "var(--gap-sm)" }}>
        <div style={{ width: "var(--width-24)", flexShrink: 0, whiteSpace: "nowrap" }}>
          <Label>{`Žingsnis ${stepNumber}`}</Label>
        </div>
        <div style={{ flexGrow: 1 }}>
          <Input
            value={step.label}
            onChange={(e) => onLabelChange(e.target.value)}
            placeholder="Žingsnio pavadinimas"
          />
        </div>
        <Button inline icon="trashIcon" onClick={onDelete} />
      </div>

      {step.options.map((option, optIdx) => (
        <div
          key={optIdx}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--gap-sm)",
            paddingLeft: "var(--width-24)",
          }}
        >
          <div style={{ width: "var(--width-24)", flexShrink: 0, whiteSpace: "nowrap" }}>
            <Label>{`Pasirinkimas ${optIdx + 1}`}</Label>
          </div>
          <div style={{ flexGrow: 1 }}>
            <Input value={option} disabled />
          </div>
          <Button inline icon="trashIcon" onClick={() => onDeleteOption(optIdx)} />
        </div>
      ))}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--gap-sm)",
          paddingLeft: "var(--width-24)",
        }}
      >
        <div style={{ width: "var(--width-24)", flexShrink: 0 }} />
        <div style={{ flexGrow: 1 }}>
          <Input
            value={newOption}
            onChange={(e) => setNewOption(e.target.value)}
            placeholder="Naujas pasirinkimas"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onAddOption(newOption);
                setNewOption("");
              }
            }}
          />
        </div>
        <Button
          inline
          emphasized
          icon="plus"
          onClick={() => {
            onAddOption(newOption);
            setNewOption("");
          }}
        >
          Pridėti pasirinkimą
        </Button>
      </div>
    </div>
  );
};

export default WizardEditorModal;
